import { useQueryClient } from '@tanstack/react-query'
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools'
import { ProductTierSku, Role } from '@vendia/management-api-types'
import { signOut } from 'aws-amplify/auth'
import debug from 'debug'
import { decodeJwt } from 'jose'
import React, { useEffect, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useRecoilState } from 'recoil'

import { authRoutes } from './app.router'
import Icon from './components/icons/icon'
import { EnterpriseModal } from './components/modals/enterprise-modal'
import { UserQualificationModal } from './components/modals/user-qualification-modal'
import { UNI_SELECT_ID } from './components/navigation/uni-dropdown'
import _config from './config'
import posthog from 'posthog-js'
import analytics from './utils/analytics'
import { authContext, AuthStatus, getStoredUserAuth } from './utils/auth/auth-context'
import { OPTIV_STAGING_ORG_ID, setStoredOrgId } from './utils/auth/auth.utils'
import clearAuth from './utils/auth/clearStorage'
import { markQualificationComplete } from './utils/auth/new-user-signup'
import { checkVerification, getUserMFASettings, refreshSession } from './utils/auth/cognito'
import useGetCurrentVendiaUserQuery from './utils/hooks/use-current-vendia-user-query'
import useFeatureToggle, { Features } from './utils/hooks/use-feature-toggle'
import { useGetOrg } from './utils/hooks/use-get-org'
import { useIsEnterpriseOrgMember } from './utils/hooks/use-is-org-member'
import { useListConsumerDistributions } from './utils/hooks/use-list-consumer-distributions'
import useListUnis from './utils/hooks/use-list-unis'
import { useQualification } from './utils/hooks/use-mcp-user-qualification-info'
import { getStoredUtmParams } from './utils/hooks/use-stored-utm-params'
import { getRedirectUrl } from './utils/hooks/use-track-last-visited'
import { onRouteChange } from './utils/router/on-route-change'
import { selectedRoleState, showQueryDevToolsState, userState } from './utils/state'
import { ENTERPRISE_TIERS, Tier } from './utils/subscription'

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[]
  }
}

const logger = debug('app:app.tsx')
const authLogger = debug('auth:main')
const upgradeLogger = debug('app:upgrade')

enum Status {
  INITIALIZED,
  ENTERPRISE_TRIAL_EXPIRED,
}

function App() {
  // Open uni switcher, may graduate to full "cmd+k" menu one day...
  // "mod+k" is cross-platform (ctrl+k on Windows, cmd+k on Mac)
  useHotkeys(['mod+k'], () => {
    const uniSelect = document.getElementById(UNI_SELECT_ID)
    if (uniSelect) {
      uniSelect.click()
    }
  })

  const { isEnabled } = useFeatureToggle(Features.DEV_SETTINGS)

  const [showQueryDevTools, toggleShowQueryDevTools] = useRecoilState(showQueryDevToolsState)
  const [searchParams] = useSearchParams()

  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(true)
  const hasRedirectedRef = React.useRef(false)
  const location = useLocation()
  const navigate = useNavigate()

  const { setAuthStatus, setUnauthStatus, auth } = React.useContext(authContext)

  const [user, setUserState] = useRecoilState<any>(userState)
  const [selectedRole, setSelectedRole] = useRecoilState(selectedRoleState)

  const { getCurrentVendiaUserQuery } = useGetCurrentVendiaUserQuery()
  const { isQualificationInfoComplete } = useQualification()
  const getOrg = useGetOrg()
  const org = getOrg?.data?.getOrganization
  const { data: isEnterprise, isLoading: isLoadingEnterpriseStatus } = useIsEnterpriseOrgMember()
  const { listUnisQuery } = useListUnis({ isAuthenticated: auth.authenticated })
  const unis = listUnisQuery.data
  const isLoadingUnis = listUnisQuery.isLoading
  const isNewUserSignup = localStorage.getItem('mcp_new_user_signup') === 'true'
  const [isQualificationDismissed, setIsQualificationDismissed] = useState(false)
  const getUser = getCurrentVendiaUserQuery?.data?.getUser
  const roles = getUser?.roles as Role[]

  useEffect(() => {
    // Once getUser loads and roles are available, set the selected role
    // and fix issues with stale selected role name in localStorage
    const storedRoleName = localStorage.getItem(`selectedRoleName:${getUser?.userId}`)
    if (!selectedRole.name && roles && roles.length > 0) {
      logger('No selected role, but roles are loaded...')
      // If user selected a role in a previous session AND it still exists, set it as the selected role
      if (storedRoleName && roles.find((role) => role.name === storedRoleName)) {
        logger('Setting selected role to stored role name', storedRoleName)
        setSelectedRole({ name: storedRoleName })
      } else {
        // Otherwise, set the default role as the selected role
        const defaultRole = roles.find((role) => role.isDefault)
        logger('Setting selected role to default role:', defaultRole?.name ?? roles[0].name)
        setSelectedRole({ name: defaultRole?.name ?? roles[0].name })
        // Also, remove possibly stale role name from localStorage
        localStorage.removeItem(`selectedRoleName:${getUser?.userId}`)
      }
    }
  }, [roles, selectedRole, setSelectedRole, getUser?.userId])

  // Store orgId in localStorage for session timeout logic (Optiv staging org only)
  useEffect(() => {
    if (org?.orgId && org.orgId === OPTIV_STAGING_ORG_ID) {
      setStoredOrgId(org.orgId)
    }
  }, [org?.orgId])

  /* If user creates new account & runs through sign up flow in same browser instance */
  async function handleSignupLogoutRedirect(authInfo: typeof auth) {
    if (!authInfo.authenticated) {
      return
    }
    if (location.pathname === '/signup' && location.hash.startsWith('#code') && authInfo.authenticated) {
      // New account flow
      await signOut()
      setUnauthStatus()
      navigate(`${location.pathname}${location.hash}`)
    }
  }

  handleSignupLogoutRedirect(auth)

  /* Run effect once */
  useEffect(() => {
    /* Skip all analytics for internal users (?internal=true) */
    if (!_config.isInternalUser) {
      /* Listen to SPA route changes and track page views */
      onRouteChange((_newRoutePath) => {
        analytics.page()
      })
    }

    /* Load icon sprite once on app start */
    Icon.loadSprite()
  }, [])

  /* Set user & auth context on auth changes and page reloads */
  useEffect(() => {
    onAuthChange()
  }, [auth.authenticated])

  useEffect(() => {
    if (!auth.emailVerified) {
      if (getOrg?.data?.getOrganization) {
        const org = getOrg?.data?.getOrganization
        const domain = auth.contactDetails?.unverified?.email?.split('@')[1]
        if (domain && org?.domains?.includes(domain)) {
          // Email should be verified
          const newContactDetails = {
            verified: {
              email: auth.contactDetails.unverified?.email,
              phoneNumber: auth.contactDetails.unverified?.phoneNumber,
            },
          }

          setAuthStatus({
            ...auth,
            emailVerified: true,
            contactDetails: newContactDetails,
          })

          auth.contactDetails = newContactDetails

          setUserState({
            ...auth,
            emailVerified: true,
            contactDetails: newContactDetails,
            sub: auth.userId,
          })
        }
      }
    }
  }, [auth, getOrg])

  async function onAuthChange() {
    try {
      const currentSession = getStoredUserAuth()

      if (!currentSession.authenticated) {
        setIsLoading(false)
        return
      }

      setUserState({
        ...currentSession,
        sub: currentSession.userId,
      })

      /* Set __anon_id if its missing or different */
      const currentAnonId = analytics.storage.getItem('__anon_id')

      const idTokenPayload = decodeJwt(currentSession.idToken)

      const cognitoAnonId = idTokenPayload?.['custom:anonId'] as string | undefined
      if (cognitoAnonId && currentAnonId !== cognitoAnonId) {
        authLogger('Set __anon_id to value from cognito', cognitoAnonId)
        // @ts-expect-error AnalyticsInstance doesn't have setAnonymousId
        analytics.setAnonymousId(cognitoAnonId)
      }
      /* Set __user_id if its missing or different */
      const currentUserId = analytics.storage.getItem('__user_id')
      const cognitoUserId = idTokenPayload.sub as string
      if (cognitoUserId && currentUserId !== cognitoUserId) {
        authLogger('Set __user_id to value from cognito', currentUserId)
        analytics.storage.setItem('__user_id', cognitoUserId)
      }

      const [isVerified, contactDetails] = checkVerification(idTokenPayload)

      const mfaSettings = await getUserMFASettings(currentSession.accessToken.jwtToken)

      const timestamp = idTokenPayload['custom:timestamp'] as number | undefined
      setAuthStatus({
        userId: cognitoUserId,
        email: currentSession.email,
        type: currentSession.type,
        idToken: currentSession.idToken,
        accessToken: currentSession.accessToken,
        refreshToken: currentSession.refreshToken,
        timestamp: timestamp ?? 0,
        authenticated: true,
        emailVerified: isVerified,
        contactDetails: contactDetails,
        mfa: mfaSettings,
      })
      posthog.identify(cognitoUserId, {
        email: currentSession.email,
        $internal_or_test_user: _config.isInternalUser,
      })
    } catch (error: any) {
      authLogger(error)

      if (error === 'NotAuthorizedException' || error.name === 'NotAuthorizedException') {
        authLogger('<App /> loaded. NotAuthorizedException')
        const newSession = await refreshSession()
        setAuthStatus(newSession as AuthStatus)
      }

      if (error === 'No current user' || error.code === 'UserNotFoundException') {
        authLogger('<App /> loaded. No current user via onAuthChange')
        clearAuth()
        setUnauthStatus()
        posthog.reset()
      }
    }
    setIsLoading(false)
  }

  // Check to see if user is in expired trial and show paywall that blocks UI if so
  const [status, setStatus] = useState(Status.INITIALIZED)
  const subscriptionTier = getOrg?.data?.getOrganization?.subscription?.tier
  const subscriptionExpiry = getOrg?.data?.getOrganization?.subscription?.expiry
  const { data } = useListConsumerDistributions()
  const distributions = data?.listDistributions?.distributions
  useEffect(() => {
    const asyncUseEffect = async () => {
      if (!subscriptionTier || !distributions) {
        return
      }
      logger('Checking subscription tier', subscriptionTier)

      const expiryTime = Date.parse(subscriptionExpiry ?? '')
      const isExpired = !Number.isNaN(expiryTime) && Date.now() - expiryTime > 0

      // TODO: remove this logic if/when we're sure we're not going back to the old model
      // We are currently treating INDIVIDUAL/INVIDUAL_TRIAL as "no access" and requiring enterprise reachout, onboarding, etc.
      // Accounts will be upgraded by CLI tooling to ENTERPRISE_TRIAL or ENTERPRISE to grant access to the app.
      // --UNLESS-- another vendia org has shared a data product with this user, then route them to the data products page!
      // if (
      //   subscriptionTier !== Tier.ENTERPRISE_TRIAL &&
      //   subscriptionTier !== Tier.ENTERPRISE &&
      //   subscriptionTier !== Tier.INDIVIDUAL &&
      //   distributions.length === 0
      // ) {
      //   setStatus(Status.NEEDS_ENTERPRISE_ONBOARDING)
      if (subscriptionTier === Tier.ENTERPRISE_TRIAL && isExpired) {
        setStatus(Status.ENTERPRISE_TRIAL_EXPIRED)
      } else {
        setStatus(Status.INITIALIZED)
        upgradeLogger('upgrade not needed, set to initialized')
      }
    }
    asyncUseEffect()
  }, [subscriptionTier, distributions])

  useEffect(() => {
    if (window.Cypress) {
      // @ts-ignore small hack to allow forcing data refresh in Cypress tests
      window.queryClient = queryClient
    }
  }, [queryClient])

  useEffect(() => {
    // Initialize Pendo for ALL environments except automation (Cypress)
    logger('Pendo init attempt...', {
      'import.meta.env.NEXT_PUBLIC_VENDIA_ENV': import.meta.env.NEXT_PUBLIC_VENDIA_ENV,
      'import.meta.env.DEV': import.meta.env.DEV,
      authenticated: auth.authenticated,
      org: Boolean(org),
      isCypress: Boolean(window.Cypress),
    })
    if (auth.authenticated && org && !window.Cypress && !_config.isInternalUser) {
      try {
        logger('Initializing pendo...')
        // Get UTM params from first-visit attribution data stored in localStorage
        const utmParams = getStoredUtmParams()
        // @ts-expect-error globally available
        pendo.initialize({
          visitor: {
            id: auth.userId,
            email: auth.email,
            paidOrTrialUser: org.subscription?.tier,
            environment: import.meta.env.NEXT_PUBLIC_VENDIA_ENV,
            // Include UTM params from first visit for attribution tracking
            ...utmParams,
          },
          account: {
            id: org.orgId,
            accountName: org.name,
            payingStatus: org.subscription?.tier,
          },
        })
      } catch (e) {
        console.error('Error initializing pendo:', e)
      }
    }

    // Push userTier to GTM dataLayer for conditional tag loading
    if (auth.authenticated && org) {
      const productTierSku = org.productTier?.sku as ProductTierSku | undefined
      let userTier = 'INDIVIDUAL'
      if (productTierSku) {
        if (productTierSku === ProductTierSku.Pro) {
          userTier = 'PRO'
        } else if (productTierSku === ProductTierSku.Enterprise) {
          userTier = 'ENTERPRISE'
        }
      } else if (ENTERPRISE_TIERS.includes(org.subscription?.tier as Tier)) {
        userTier = 'ENTERPRISE'
      }
      window.dataLayer?.push({ userTier })

      // Enable PostHog session recording for free tier users only
      if (userTier === 'INDIVIDUAL' && !_config.isInternalUser) {
        posthog.startSessionRecording(true)
      }
    }
  }, [auth, org])

  // Redirect to login if not authenticated or email not verified
  useEffect(() => {
    const redirectParam = searchParams.get('redirect')
    const redirectPath = redirectParam ?? `${location.pathname}${location.search}`
    const { pathname } = location

    // Compute isAuthRoute once and reuse
    const isAuthRoute = authRoutes.some(
      (route) => pathname === route || pathname === `${route}/` || pathname.startsWith(`${route}#`),
    )

    // Redirect authenticated users away from auth routes (e.g., when they use browser back button)
    // Exclude /logout and /callback as these are intentional routes for authenticated users
    if (auth.authenticated && isAuthRoute) {
      // Excluded routes that authenticated users can access
      if (pathname !== '/logout' && pathname !== '/callback' && pathname !== '/oauth/callback') {
        const redirectUrl = getRedirectUrl(auth.email) ?? '/'
        navigate(redirectUrl, { replace: true })
        return
      }
    }

    if (!auth.authenticated && !isAuthRoute) {
      navigate(`/login?redirect=${redirectPath}`)
    }
    if (Boolean(org?.orgId) && auth.authenticated && auth.type !== 'SSO' && auth.emailVerified === false) {
      navigate(`/verify-email?redirect=${redirectPath}`)
    }

    // Redirect non-enterprise (MCP) users to their first uni upon initial login only
    // Skip redirect if there's an explicit redirect parameter (e.g., deep links)
    const redirectKey = `non_enterprise_initial_redirect_${auth.userId}`
    const hasExplicitRedirect = redirectParam && redirectParam !== '/'
    const hasAlreadyRedirected = sessionStorage.getItem(redirectKey) || hasRedirectedRef.current

    if (
      auth.authenticated &&
      org?.orgId &&
      !isLoadingEnterpriseStatus &&
      !isLoadingUnis &&
      isEnterprise === false &&
      unis &&
      unis.length > 0 &&
      !hasAlreadyRedirected &&
      !isAuthRoute &&
      !hasExplicitRedirect &&
      location.pathname === '/'
    ) {
      const firstUni = unis[0]
      const uniName = firstUni.name?.replace('.unis.vendia.net', '')
      if (uniName) {
        hasRedirectedRef.current = true
        sessionStorage.setItem(redirectKey, 'true')
        navigate(`/uni/${uniName}`)
      }
    }
  }, [
    auth,
    org?.orgId,
    status,
    location.pathname,
    isEnterprise,
    unis,
    navigate,
    isLoadingEnterpriseStatus,
    isLoadingUnis,
  ])

  useEffect(() => {
    if (!location.pathname.includes('evolve')) {
      Object.keys(localStorage).forEach((key) => {
        if (key.endsWith('evolve-schema')) {
          localStorage.removeItem(key)
        }
      })
    }
  }, [location])

  return (
    !isLoading && (
      <div className='flex min-h-screen min-w-[1080px] flex-col' data-testid='app'>
        <Outlet context={{ auth, isOrgLoaded: Boolean(org?.orgId) }} />

        <EnterpriseModal isTrialExpired isOpen={status === Status.ENTERPRISE_TRIAL_EXPIRED} />
        <UserQualificationModal
          isOpen={
            auth.authenticated &&
            Boolean(org?.orgId) &&
            Boolean(getCurrentVendiaUserQuery?.data?.getUser?.userId) &&
            !isLoadingEnterpriseStatus &&
            isEnterprise === false &&
            !isQualificationInfoComplete &&
            isNewUserSignup &&
            !isQualificationDismissed
          }
          onComplete={() => {
            markQualificationComplete()
            setIsQualificationDismissed(true)
          }}
        />
        {isEnabled && showQueryDevTools && (
          <ReactQueryDevtoolsPanel
            style={{ position: 'absolute', width: '100%', left: 0, bottom: 0 }}
            onClose={() => toggleShowQueryDevTools(false)}
          />
        )}
      </div>
    )
  )
}

export default App
